import React, { useState, useEffect, useCallback, useRef } from 'react';
import { powerLocationStatusAPI } from '../../services/api';
import VWorldMap from './VWorldMap';
import PowerPlantCard from './PowerPlantCard';
import SummaryStats from './SummaryStats';
import FilterPanel from './FilterPanel';
import './SolarDashboard.css';

const SolarDashboard = ({ user, onLogout }) => {
  const [powerPlants, setPowerPlants] = useState([]);
  const [filteredPlants, setFilteredPlants] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [summaryData, setSummaryData] = useState({
    totalCount: 0,
    normalCount: 0,
    errorCount: 0,
    totalPowerGeneration: 0,
    averageEfficiencyPercentage: 0
  });
  const [filters, setFilters] = useState({
    region: 'all',
    status: 'all',
    search: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [after, setAfter] = useState(null);
  const isInitialLoad = useRef(true);

  // 발전소 상태 목록 로드 (무한 스크롤)
  const loadPowerPlantStatus = useCallback(async (reset = false) => {
    if (isLoading) {
      console.log('Already loading, skipping request');
      return;
    }
    
    console.log('loadPowerPlantStatus called - reset:', reset, 'loading:', isLoading);
    setIsLoading(true);
    
    try {
      const condition = {
        region: filters.region === 'all' ? null : filters.region,
        status: filters.status === 'all' ? null : filters.status,
        keyword: filters.search,
        cursor: reset ? null : cursor,
        after: reset ? null : after,
        sortBy: 'createdAt',
        sortDirection: 'DESC',
        limit: null,
      };
      
      console.log('API call condition:', condition);
      const response = await powerLocationStatusAPI.getStatusList(condition);
      
      console.log('SolarDashboard - API response:', response);
      console.log('SolarDashboard - Power plants data:', response.data);
      
      // API 데이터를 기존 형식으로 변환
      const transformedData = response.data.map(plant => ({
        id: plant.id,
        name: plant.name,
        region: plant.region || '서울', // 백엔드에서 지역 정보 제공 시 사용
        location: { lat: plant.latitude, lng: plant.longitude },
        status: plant.overallStatus.toLowerCase().replace('_error', '_error'),
        inverter: {
          total: plant.inverterStatus?.total || 0,
          working: plant.inverterStatus?.current || 0
        },
        environmentSensor: {
          total: plant.environmentalSensorStatus?.total || 0,
          working: plant.environmentalSensorStatus?.current || 0
        },
        connectionBox: {
          total: plant.connectionBoxStatus?.total || 0,
          working: plant.connectionBoxStatus?.current || 0
        },
        channel: {
          total: plant.channelStatus?.total || 0,
          working: plant.channelStatus?.current || 0
        },
        communicationStatus: plant.communicationStatus === '정상' ? 'normal' : 'disconnected',
        powerGeneration: plant.powerGenerationKw,
        efficiency: plant.efficiencyPercentage
      }));
      
      if (reset) {
        console.log('Resetting power plants with:', transformedData);
        setPowerPlants(transformedData || []);
        setFilteredPlants(transformedData || []);
      } else {
        console.log('Adding to existing power plants:', transformedData);
        setPowerPlants(prev => {
          // 중복 제거: id를 기준으로 중복된 항목 제거
          const existingIds = new Set(prev.map(plant => plant.id));
          const newPlants = (transformedData || []).filter(plant => !existingIds.has(plant.id));
          const newData = [...prev, ...newPlants];
          console.log('New power plants state (duplicates removed):', newData);
          return newData;
        });
        setFilteredPlants(prev => {
          // 중복 제거: id를 기준으로 중복된 항목 제거
          const existingIds = new Set(prev.map(plant => plant.id));
          const newPlants = (transformedData || []).filter(plant => !existingIds.has(plant.id));
          const newData = [...prev, ...newPlants];
          return newData;
        });
      }
      
      setHasMore(response.hasNext || false);
      setCursor(response.nextCursor || null);
      setAfter(response.createdAt || null);
      
      // 전체 요약 데이터 업데이트 (첫 번째 로드 또는 리셋 시에만)
      if (reset || isInitialLoad.current) {
        setSummaryData({
          totalCount: response.totalCount || 0,
          normalCount: response.normalCount || 0,
          errorCount: response.errorCount || 0,
          totalPowerGeneration: response.totalPowerGeneration || 0,
          averageEfficiencyPercentage: response.averageEfficiencyPercentage || 0
        });
      }
    } catch (error) {
      console.error('발전소 상태 목록 로드 실패:', error);
      alert('발전소 상태 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [filters, cursor, after, isLoading]);

  // 초기 데이터 로드 (한 번만 실행)
  useEffect(() => {
    if (isInitialLoad.current) {
      console.log('useEffect triggered - initial load');
      loadPowerPlantStatus();
      isInitialLoad.current = false;
    }
  }, [loadPowerPlantStatus]);

  // 필터 변경 시 데이터 새로고침
  useEffect(() => {
    if (!isInitialLoad.current) {
      console.log('Filter changed, resetting data...');
      setPowerPlants([]);
      setFilteredPlants([]);
      setCursor(null);
      setAfter(null);
      setHasMore(true);
      loadPowerPlantStatus(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.region, filters.status, filters.search]);

  // 더 많은 데이터 로드 (무한스크롤)
  const loadMore = () => {
    if (hasMore && !isLoading) {
      loadPowerPlantStatus();
    }
  };

  // 스크롤 이벤트 핸들러
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop === clientHeight && hasMore && !isLoading) {
      loadMore();
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handlePlantSelect = (plant) => {
    setSelectedPlant(plant);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return '#10B981';
      case 'partial_error': return '#F59E0B';
      case 'communication_error': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'normal': return '정상';
      case 'partial_error': return '부분 오류';
      case 'communication_error': return '통신 오류';
      default: return '알 수 없음';
    }
  };

  return (
    <div className="solar-dashboard">
      <div className="dashboard-content">
        <div className="solar-left-panel">
          <SummaryStats summaryData={summaryData} />
          <FilterPanel 
            filters={filters} 
            onFilterChange={handleFilterChange}
            regions={[...new Set(powerPlants.map(p => p.region))]}
          />
        </div>

        <div className="solar-center-panel">
          <div className="power-plant-list" onScroll={handleScroll}>
            <h3>발전소 목록</h3>
            <div className="plant-cards">
              {filteredPlants.map(plant => (
                <PowerPlantCard
                  key={plant.id}
                  plant={plant}
                  isSelected={selectedPlant?.id === plant.id}
                  onClick={() => handlePlantSelect(plant)}
                  statusColor={getStatusColor(plant.status)}
                  statusText={getStatusText(plant.status)}
                />
              ))}
            </div>
            
            {/* 무한스크롤 로딩 */}
            {isLoading && (
              <div className="loading-row">
                <div className="loading-spinner"></div>
                <span>로딩 중...</span>
              </div>
            )}
            
            {/* 더 보기 버튼 */}
            {hasMore && !isLoading && (
              <div className="load-more-section">
                <button onClick={loadMore} className="btn btn-secondary">
                  더 보기
                </button>
              </div>
            )}
            
            {/* 더 이상 데이터가 없을 때 */}
            {!hasMore && powerPlants.length > 0 && (
              <div className="no-more-data">
                <span>모든 발전소를 불러왔습니다.</span>
              </div>
            )}
          </div>
        </div>

        <div className="solar-right-panel">
          <VWorldMap
            plants={filteredPlants}
            selectedPlant={selectedPlant}
            onPlantSelect={handlePlantSelect}
            getStatusColor={getStatusColor}
          />
        </div>
      </div>
    </div>
  );
};

export default SolarDashboard;
